import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  applyForMembership,
  getMyMembershipStatus,
  initializeMembershipPayment,
  downloadWalletCard,
  downloadAppleWalletPass,
  getMembershipPricing,
} from '../services/membershipService';
import COUNTRIES, { findCountryByName } from '../utils/countries';
import { isValidPhone } from '../utils/phone';
import './Membership.css';
import '../styles/membership-modern.css';
import '../styles/membership-v2.css';

const fullName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
const normalizePhone = (p) => {
  const digits = (p || '').replace(/[^\d]/g, '');
  if (!digits) return '';
  // Egyptian local format (e.g. 01226294984) -> international (201226294984)
  if (digits.startsWith('0')) return '20' + digits.slice(1);
  return digits;
};
const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : '');
const MIN_AGE_YEARS = 23;
const maxBirthdate = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - MIN_AGE_YEARS);
  return d.toISOString().slice(0, 10);
};
const isValidBirthdate = (v) => !v || v <= maxBirthdate();
const SOCIAL_LINK_REGEX = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+(\/[^\s]*)?$/;
const isValidSocialLink = (v) => !v || SOCIAL_LINK_REGEX.test(v.trim());
const pickNonEmpty = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''));

// Admin rich-text (Quill) can save non-breaking spaces and inline white-space:
// nowrap, both of which make a line unbreakable so it runs off the box instead
// of wrapping. Normalize them to regular, breakable whitespace before render so
// the text wraps by whole words and stays inside the card.
const sanitizeInfoHtml = (html) =>
  (html || '')
    .replace(/&nbsp;|&#160;|&#xA0;| /gi, ' ')
    .replace(/[   ⁠﻿​]/g, ' ')
    .replace(/[   ⁠​﻿]/g, ' ')
    .replace(/white-space\s*:\s*nowrap/gi, 'white-space:normal')
    .replace(/word-break\s*:\s*break-all/gi, 'word-break:normal');

// ---------- field validators ----------
// Letters (any language), spaces, hyphens, apostrophes and dots only.
const NAME_REGEX = /^[\p{L}][\p{L} .'-]*$/u;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const nameError = (v, label = 'Full name') => {
  const val = (v || '').trim();
  if (!val) return `${label} is required.`;
  if (val.length < 3) return `${label} must be at least 3 characters.`;
  if (val.length > 60) return `${label} must be 60 characters or fewer.`;
  if (!NAME_REGEX.test(val)) return `${label} can only contain letters, spaces, hyphens and apostrophes.`;
  return '';
};
const emailError = (v) => {
  const val = (v || '').trim();
  if (!val) return 'Email is required.';
  if (!EMAIL_REGEX.test(val)) return 'Please enter a valid email address (e.g. name@example.com).';
  return '';
};
const phoneError = (v, label = 'Phone number') => {
  const digits = (v || '').replace(/\D/g, '');
  if (!digits) return `${label} is required.`;
  if (!isValidPhone(v)) return 'Please enter a valid phone number for the selected country';
  return '';
};
const socialError = (v, label = 'Social media link', example = 'https://instagram.com/yourhandle') => {
  const val = (v || '').trim();
  if (!val) return `${label} is required.`;
  if (!isValidSocialLink(val)) return `Please enter a valid link (e.g. ${example}).`;
  return '';
};

// Photo upload constraints — must match backend membership-photo filter / limits.
const ALLOWED_PHOTO_MIMES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // 5 MB
const photoFileError = (file, label = 'Photo') => {
  if (!file) return `${label} is required.`;
  if (!ALLOWED_PHOTO_MIMES.includes(file.type)) {
    return `${label} must be a JPEG, PNG or WebP image.`;
  }
  if (file.size > MAX_PHOTO_BYTES) return `${label} must be 5 MB or smaller.`;
  return '';
};

// Order used to scroll to the first field with an error.
const FIELD_ORDER = [
  'name', 'email', 'phone', 'socialMediaLink', 'birthdate', 'photo',
  'coupleName', 'couplePhone', 'coupleEmail', 'coupleSocialMediaLink', 'couplePhoto',
];

const Membership = () => {
  const { isLoggedIn, user } = useAuth();
  const { path, orgSlug } = useOrg();
  const navigate = useNavigate();
  // Paymob redirects back to /o/:slug/membership?payment=success|failed|... —
  // we render a full-width confirmation banner at the top and then strip
  // the query params so a page refresh doesn't re-show the message.
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentStatus = searchParams.get('payment');
  const paymentMessage = searchParams.get('message');
  useEffect(() => {
    if (!paymentStatus) return;
    // Refresh the guest's membership status so the widget flips into the
    // active/pending view (whatever Paymob's callback set on the backend).
    fetchMembershipStatus();
    // Clean the URL after a short delay so the banner shows briefly and the
    // guest can bookmark the clean URL.
    const t = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      next.delete('message');
      setSearchParams(next, { replace: true });
    }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [pricing, setPricing] = useState(null);
  // Drives the Phone Number input's country flag/code. Defaults to UAE and is
  // updated when the user picks a Country of Residence below.
  const [phoneCountry, setPhoneCountry] = useState('ae');

  const [formData, setFormData] = useState({
    type: 1, // 1 = SINGLE, 2 = COUPLE
    categoryId: null, // set → applying for a custom guest-bookable type (single-person)
    authCapture: true, // Always true - payment before admin approval
    name: '',
    phone: '',
    email: '',
    socialMediaLink: '',
    addressStreet: '',
    addressNeighborhood: '',
    addressMoreInfo: '',
    residenceCountry: '',
    birthdate: '',
    gender: 1, // 1 = MALE, 2 = FEMALE
    // Couple fields (only if type === 2)
    coupleName: '',
    couplePhone: '',
    coupleEmail: '',
    coupleSocialMediaLink: '',
  });

  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [couplePhotoFile, setCouplePhotoFile] = useState(null);
  const [couplePhotoPreview, setCouplePhotoPreview] = useState('');
  const [showAllBenefits, setShowAllBenefits] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate(path('/login'));
      return;
    }
    fetchMembershipStatus();
    fetchPricing();
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    const fromUser = pickNonEmpty({
      name: fullName(user),
      email: user?.email,
      phone: normalizePhone(user?.phoneNumber),
    });

    const m = membershipStatus?.membership;
    const fromMembership = m
      ? pickNonEmpty({
          type: m.type != null ? Number(m.type) : undefined,
          name: m.name,
          email: m.email,
          phone: normalizePhone(m.phone),
          socialMediaLink: m.socialMediaLink,
          birthdate: toDateInput(m.birthdate),
          gender: m.gender != null ? Number(m.gender) : undefined,
          addressStreet: m.addressStreet,
          addressNeighborhood: m.addressNeighborhood,
          addressMoreInfo: m.addressMoreInfo,
          residenceCountry: m.residenceCountry,
          coupleName: m.coupleName,
          couplePhone: normalizePhone(m.couplePhone),
          coupleEmail: m.coupleEmail,
          coupleSocialMediaLink: m.coupleSocialMediaLink,
        })
      : {};

    setFormData((prev) => ({ ...prev, ...fromUser, ...fromMembership }));
  }, [user, membershipStatus]);

  const fetchMembershipStatus = async () => {
    try {
      setLoading(true);
      const data = await getMyMembershipStatus();
      setMembershipStatus(data);
      // Show form if user can apply OR if there's no membership at all
      setShowForm(data.canApply === true || (data.status === 'none' && !data.hasMembership));
    } catch (err) {
      console.error('Error fetching membership status:', err);
      setError('Failed to load membership status');
      // On error, show the form anyway so user can try to apply
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = async () => {
    try {
      // Pass the URL orgSlug so the backend returns THIS org's membership
      // settings (title, slogan, currency, categories, benefits) instead of
      // falling back to global defaults. Without this, /o/doomzil/membership
      // was showing "Membership" / "Join our family..." even after the admin
      // set custom values under the Doomzil org.
      const data = await getMembershipPricing(orgSlug);
      setPricing(data);
      // Pre-select a tier so the form is never in a "nothing picked" state
      // (that produced EGP 0 applications when guests skipped the tier
      // selector and submitted with default type=SINGLE, categoryId=null).
      // Prefer the first active category; fall back to Single/Couple when
      // the org runs only default tiers.
      const cats = Array.isArray(data?.categories) ? data.categories : [];
      setFormData((prev) => {
        if (prev.categoryId) return prev;
        if (cats.length > 0) {
          const first = cats[0];
          return {
            ...prev,
            categoryId: first.id,
            type: first.baseType === 'couple' ? 2 : 1,
          };
        }
        return prev;
      });
    } catch (err) {
      console.error('Error fetching pricing:', err);
      // Use default pricing if fetch fails
      setPricing({
        single: { priceUsd: 150, duration: 12 },
        couple: { priceUsd: 250, duration: 12 },
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear this field's error as soon as the user starts correcting it.
    setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  };

  const handlePhoneChange = (value, field) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  // Selecting a Country of Residence also sets the Phone Number field's country:
  // we update the phone input's country (flag) and reset the phone to that
  // country's dial code so the prefix matches the selection.
  const handleResidenceCountryChange = (e) => {
    const name = e.target.value;
    const country = findCountryByName(name);
    setFormData((prev) => ({
      ...prev,
      residenceCountry: name,
      ...(country ? { phone: country.dialCode } : {}),
    }));
    if (country) setPhoneCountry(country.iso2);
    setFieldErrors((prev) => (prev.phone ? { ...prev, phone: '' } : prev));
  };

  const handlePhotoChange = (e, field) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const label = field === 'couplePhoto' ? "Partner's photo" : 'Photo';
    const err = photoFileError(file, label);
    if (err) {
      setFieldErrors((prev) => ({ ...prev, [field]: err }));
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      if (field === 'couplePhoto') {
        setCouplePhotoFile(file);
        setCouplePhotoPreview(reader.result);
      } else {
        setPhotoFile(file);
        setPhotoPreview(reader.result);
      }
    };
    reader.readAsDataURL(file);
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const handleRemovePhoto = (field) => {
    if (field === 'couplePhoto') {
      setCouplePhotoFile(null);
      setCouplePhotoPreview('');
    } else {
      setPhotoFile(null);
      setPhotoPreview('');
    }
  };

  // Validate every required field and return a { fieldName: message } map.
  const validateForm = () => {
    const errs = {
      name: nameError(formData.name),
      email: emailError(formData.email),
      phone: phoneError(formData.phone),
      socialMediaLink: socialError(formData.socialMediaLink),
      birthdate:
        formData.birthdate && !isValidBirthdate(formData.birthdate)
          ? `You must be at least ${MIN_AGE_YEARS} years old.`
          : '',
      photo: photoFileError(photoFile),
    };
    if (formData.type === 2) {
      errs.coupleName = nameError(formData.coupleName, "Partner's full name");
      errs.couplePhone = phoneError(formData.couplePhone, "Partner's phone");
      errs.coupleEmail = emailError(formData.coupleEmail);
      errs.coupleSocialMediaLink = socialError(
        formData.coupleSocialMediaLink,
        "Partner's social media link",
        'https://instagram.com/partnerhandle',
      );
      errs.couplePhoto = photoFileError(couplePhotoFile, "Partner's photo");
    }
    return Object.fromEntries(Object.entries(errs).filter(([, v]) => v));
  };

  // Validate a single field on blur so users get feedback as they go.
  const validateSingle = (field) => {
    const errs = validateForm();
    setFieldErrors((prev) => ({ ...prev, [field]: errs[field] || '' }));
  };

  // Scroll to (and focus) the first field that has an error.
  const scrollToFirstError = (errs) => {
    const firstKey = FIELD_ORDER.find((k) => errs[k]);
    if (!firstKey) return;
    // On mobile, tapping submit dismisses the on-screen keyboard, and the
    // resulting viewport reflow cancels a scroll fired in the same tick. Blur the
    // active field, then defer the scroll so it runs after the reflow settles
    // (the error messages have also rendered by then, so positions are accurate).
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    window.setTimeout(() => {
      const el = document.getElementById(`field-${firstKey}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = el.querySelector('input, select, textarea');
      if (input) window.setTimeout(() => input.focus({ preventScroll: true }), 350);
    }, 250);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Field-level validation
    const errs = validateForm();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      scrollToFirstError(errs);
      return;
    }
    setFieldErrors({});

    // Guard: require a tier when categories are configured. Without this
    // the form would submit with categoryId=null and fall back to the
    // default Single/Couple tier — which may be priced 0 and cause a
    // "free" application (bug where guest was billed only commission).
    const cats = pricing?.categories || [];
    if (cats.length > 0 && !formData.categoryId) {
      setError('Please pick a membership tier before submitting.');
      const el = document.querySelector('.mv-tiers, .mv-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    try {
      setSubmitting(true);

      // Prepare data by removing empty optional fields
      const submitData = { ...formData };

      // Server-side flow is picked from the org's `paymentMode` setting.
      // Force the client's `authCapture` flag to match — this replaces the
      // old hard-coded `true` so orgs on 'manual_link' skip Paymob upfront
      // and instead wait for the admin to trigger the payment link email.
      submitData.authCapture = (pricing?.paymentMode || 'auth_capture') === 'auth_capture';

      // Tell the backend which org's settings apply. Without this the API
      // falls back to the legacy NULL-org row (which may have no Paymob
      // keys) and rejects with "Payment gateway not configured".
      if (orgSlug) submitData.orgSlug = orgSlug;

      // Remove empty optional fields to avoid validation errors
      if (!submitData.birthdate) delete submitData.birthdate;
      if (!submitData.addressStreet) delete submitData.addressStreet;
      if (!submitData.addressNeighborhood) delete submitData.addressNeighborhood;
      if (!submitData.addressMoreInfo) delete submitData.addressMoreInfo;
      if (!submitData.residenceCountry) delete submitData.residenceCountry;
      // Drop couple-only fields for single memberships so the multipart body
      // doesn't carry stale partner info if the user switched type mid-flow.
      if (submitData.type !== 2) {
        delete submitData.coupleName;
        delete submitData.couplePhone;
        delete submitData.coupleEmail;
        delete submitData.coupleSocialMediaLink;
      }

      const fd = new FormData();
      Object.entries(submitData).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') fd.append(k, v);
      });
      fd.append('photo', photoFile);
      if (formData.type === 2 && couplePhotoFile) {
        fd.append('couplePhoto', couplePhotoFile);
      }

      console.log('Submitting membership application...');
      const response = await applyForMembership(fd);
      console.log('Application response:', response);

      // Branch on the org's payment mode:
      //   auth_capture → guest is redirected to Paymob NOW to authorize the card.
      //   manual_link  → no payment yet. Application is queued for admin approval;
      //                  guest will receive an email with a payment link once approved.
      const mode = pricing?.paymentMode || 'auth_capture';

      if (mode === 'manual_link') {
        // Refresh status so the widget flips into the "pending review" view
        // (renderStatusMessage picks up hasMembership + status: PENDING).
        await fetchMembershipStatus();
        setShowForm(false);
        return;
      }

      // Auth-capture (default): must have a paymentUrl to redirect the guest to.
      if (response.paymentUrl) {
        console.log('Redirecting to payment URL:', response.paymentUrl);
        window.location.href = response.paymentUrl;
        return;
      }

      // If no payment URL (shouldn't happen for auth-capture), show error.
      setError('Payment initialization failed. Please try again or contact support.');
    } catch (err) {
      console.error('Membership application error:', err);
      console.error('Error response:', err.response);

      // Handle different error formats
      let errorMessage = 'Failed to submit application';

      if (err.response?.data) {
        const errorData = err.response.data;

        // If message is an array of validation errors
        if (Array.isArray(errorData.message)) {
          errorMessage = (
            <ul className="mb-0">
              {errorData.message.map((msg, index) => (
                <li key={index}>{msg}</li>
              ))}
            </ul>
          );
        }
        // If message is a string
        else if (typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
        // If error is a string
        else if (typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePay = async () => {
    try {
      setSubmitting(true);
      const response = await initializeMembershipPayment(membershipStatus.membership.id);
      window.location.href = response.paymentUrl;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initialize payment');
      setSubmitting(false);
    }
  };

  const handleDownloadWalletCard = async (membershipId) => {
    try {
      const response = await downloadWalletCard(membershipId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `membership-card.pdf`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading wallet card:', err);
      setError('Failed to download wallet card. Please try again.');
    }
  };

  const handleDownloadAppleWallet = async (membershipId) => {
    try {
      const response = await downloadAppleWalletPass(membershipId);
      const blob = new Blob([response.data], { type: 'application/vnd.apple.pkpass' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `membership.pkpass`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading Apple Wallet pass:', err);
      if (err.response?.status === 500) {
        setError('Apple Wallet is not configured. Please contact support.');
      } else {
        setError('Failed to add to Apple Wallet. Please try again.');
      }
    }
  };

  const renderMembershipCard = () => {
    if (!membershipStatus || !membershipStatus.membership) return null;

    const { membership } = membershipStatus;
    const isPendingCapture = membershipStatus.status === 'pending_capture';
    const displayStatusName = isPendingCapture ? 'Pending Confirmation' : membership.statusName;
    const displayStatusClass = isPendingCapture ? 'status-pending-capture' : `status-${membership.status}`;

    // Resolve the member's actual tier + benefits from the org's category
    // catalog. Falls back to the raw type name if the category isn't
    // present in pricing (e.g. the widget is showing before pricing loaded).
    const memberCategory =
      membership.categoryId && (pricing?.categories || []).find((c) => c.id === membership.categoryId);
    const displayTierName = memberCategory?.name || membership.typeName || 'Membership';
    const categoryBenefits = memberCategory?.benefits || '';

    return (
      <div className="membership-card">
        <div className="membership-header">
          <div>
            <h2>{displayTierName}</h2>
            <span className={`status-badge ${displayStatusClass}`}>
              {displayStatusName}
            </span>
          </div>
          {membership.code && (
            <div className="membership-code">
              <div className="code-label">Member Code</div>
              <div className="code-value">{membership.code}</div>
            </div>
          )}
        </div>

        <div className="membership-body">
          <div className="member-info">
            <div className="member-info-heading">
              <h3>
                <i className="bi bi-person me-2"></i>
                Member
              </h3>
              {membership.photo && (
                <img
                  src={membership.photo}
                  alt={membership.name}
                  className="member-info-photo"
                />
              )}
            </div>
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">{membership.name}</span>
            </div>
          </div>

          {String(membership.type) === '2' && (
            <div className="member-info partner-info">
              <div className="member-info-heading">
                <h3>
                  <i className="bi bi-people me-2"></i>
                  Partner
                </h3>
                {membership.couplePhoto && (
                  <img
                    src={membership.couplePhoto}
                    alt={membership.coupleName || 'Partner'}
                    className="member-info-photo"
                  />
                )}
              </div>
              <div className="info-row">
                <span className="label">Name:</span>
                <span className="value">{membership.coupleName || '—'}</span>
              </div>
            </div>
          )}

          {/* Expiry intentionally hidden from members. */}

          {/* QR code section hidden — only the member code is shown for now.
              Restore by removing this `false &&` guard. */}
          {false && membership.qrCode && (
            <div className="membership-qr-section">
              <div className="qr-label">Scan QR Code</div>
              <img src={membership.qrCode} alt="Membership QR Code" className="qr-code-image-large" />
              <p className="qr-description">Scan this code to verify your membership</p>

              {/* Wallet Actions */}
              {/* <div className="wallet-actions">
                <button
                  className="btn-wallet btn-apple-wallet"
                  onClick={() => handleDownloadAppleWallet(membership.id)}
                  title="Add to Apple Wallet"
                >
                  <i className="bi bi-apple"></i>
                  <span>Add to Apple Wallet</span>
                </button>
              </div> */}
            </div>
          )}
        </div>

        <div className="membership-footer">
          <div className="benefits">
            <h3>Your Benefits</h3>
            {/* Render the ACTUAL benefits for the member's tier from the
                admin-managed category. No more hard-coded generic list. */}
            {categoryBenefits ? (
              <div
                className="benefits-rich"
                dangerouslySetInnerHTML={{ __html: sanitizeInfoHtml(categoryBenefits) }}
              />
            ) : (
              <p className="text-muted mb-0" style={{ fontSize: '13.5px' }}>
                Your benefits will appear here once the {displayTierName} tier's
                perks are configured.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStatusMessage = () => {
    if (!membershipStatus) return null;

    if (membershipStatus.status === 'pending') {
      return (
        <div className="alert alert-info">
          <i className="bi bi-clock-history me-2"></i>
          <strong>Application Under Review</strong>
          <p>Your membership application is being reviewed by our team. You will receive an email notification once it has been processed.</p>
        </div>
      );
    }

    if (membershipStatus.status === 'approved') {
      return (
        <div className="alert alert-success">
          <i className="bi bi-check-circle me-2"></i>
          <strong>Application Approved!</strong>
          <p>Your membership has been approved. Please complete your payment to activate your membership.</p>
          <button className="btn btn-primary mt-3" onClick={handlePay} disabled={submitting}>
            {submitting ? 'Processing...' : 'Pay Now'}
          </button>
        </div>
      );
    }

    if (membershipStatus.status === 'pending_payment') {
      return (
        <div className="alert alert-warning">
          <i className="bi bi-credit-card me-2"></i>
          <strong>Payment Pending</strong>
          <p>Your payment is being processed. Your membership will be activated once approved.</p>
        </div>
      );
    }

    if (membershipStatus.status === 'pending_capture') {
      return (
        <div className="alert alert-warning">
          <i className="bi bi-hourglass-split me-2"></i>
          <strong>Payment Authorized — Awaiting Confirmation</strong>
          <p>Your payment was authorized successfully. Our team will confirm your membership shortly — you will receive an email once it is active.</p>
        </div>
      );
    }

    if (membershipStatus.status === 'rejected') {
      return (
        <div className="alert alert-danger">
          <i className="bi bi-x-circle me-2"></i>
          <strong>Application Not Approved</strong>
          <p>Unfortunately, your membership application was not approved at this time. You can apply again with updated information.</p>
          <button className="btn btn-primary mt-3" onClick={() => setShowForm(true)}>
            Apply Again
          </button>
        </div>
      );
    }

    if (membershipStatus.status === 'expired') {
      return (
        <div className="alert alert-secondary">
          <i className="bi bi-calendar-x me-2"></i>
          <strong>Membership Expired</strong>
          <p>Your membership has expired. You can apply for a new membership below.</p>
          <button className="btn btn-primary mt-3" onClick={() => setShowForm(true)}>
            Apply Again
          </button>
        </div>
      );
    }

    if (membershipStatus.status === 'cancelled') {
      return (
        <div className="alert alert-warning">
          <i className="bi bi-slash-circle me-2"></i>
          <strong>Membership Cancelled</strong>
          <p>
            {membershipStatus.message
              || 'Your previous membership was cancelled. You can apply again below.'}
          </p>
          <button className="btn btn-primary mt-3" onClick={() => setShowForm(true)}>
            Apply Again
          </button>
        </div>
      );
    }

    return null;
  };

  // Format a category's price according to the org's currency mode.
  // - 'egp'  → "EGP 1,500 / 12 months"
  // - 'usd'  → "$150 USD / 12 months"
  // - 'both' → "EGP 1,500 · $150 USD / 12 months"
  // Falls back to EGP when the mode isn't set on the org yet.
  const formatMembershipPrice = (c) => {
    const mode = pricing?.currencyMode || 'egp';
    const duration = c.duration || 12;
    const egp = Number(c.priceEgp || 0);
    const usd = Number(c.priceUsd || 0);
    const fmtEgp = `EGP ${egp.toLocaleString()}`;
    const fmtUsd = `$${usd.toLocaleString()} USD`;
    let price;
    if (mode === 'usd') price = fmtUsd;
    else if (mode === 'both') price = `${fmtEgp} · ${fmtUsd}`;
    else price = fmtEgp;
    return `${price} / ${duration} months`;
  };

  // Pull short bullet highlights out of a category's rich-HTML benefits
  // so we can show 3-4 checkmark items on the tier card. Falls back to
  // splitting by newline when the admin didn't use a bulleted list.
  const extractHighlights = (benefitsHtml) => {
    if (!benefitsHtml) return [];
    try {
      const doc = new DOMParser().parseFromString(benefitsHtml, 'text/html');
      const items = Array.from(doc.querySelectorAll('li'))
        .map((li) => (li.textContent || '').trim())
        .filter(Boolean);
      if (items.length > 0) return items.slice(0, 4);
      const text = (doc.body.textContent || '').trim();
      if (!text) return [];
      return text.split(/\n+/).map((s) => s.trim()).filter(Boolean).slice(0, 4);
    } catch (_) {
      return [];
    }
  };

  const renderTypeSelector = () => {
    const cats = pricing?.categories || [];
    if (cats.length === 0) {
      return (
        <section className="mv-section">
          <div className="mv-section-eyebrow">Choose your tier</div>
          <h2 className="mv-section-title">Pick the one that fits</h2>
          <div className="mv-empty">
            <i className="bi bi-hourglass-split" />
            <p>No membership types are available yet. Please check back later.</p>
          </div>
        </section>
      );
    }
    // Highlight the middle tier as "Most Popular" (only when there's more
    // than one). Metallic accent cycles bronze → gold → silver by index.
    const popularIdx = cats.length > 1 ? Math.floor((cats.length - 1) / 2) : -1;
    const accentByIdx = ['bronze', 'gold', 'silver'];
    return (
      <section className="mv-section">
        <div className="mv-section-eyebrow">Choose your tier</div>
        <h2 className="mv-section-title">Pick the one that fits</h2>
        <p className="mv-section-lead">
          Every tier includes priority booking. Upgrade for more benefits.
        </p>
        <div className="mv-tiers">
          {cats.map((c, idx) => {
            const highlights = extractHighlights(c.benefits);
            const isSelected = formData.categoryId === c.id;
            const isPopular = idx === popularIdx;
            const accent = accentByIdx[idx % accentByIdx.length];
            return (
              <div
                key={c.id}
                className={`mv-tier-card ${isSelected ? 'selected' : ''} ${isPopular ? 'popular' : ''}`}
                onClick={() =>
                  setFormData({
                    ...formData,
                    type: c.baseType === 'couple' ? 2 : 1,
                    categoryId: c.id,
                  })
                }
                role="button"
                tabIndex={0}
              >
                <div className={`mv-tier-accent ${accent}`} />
                {isPopular && <div className="mv-tier-badge">Most Popular</div>}
                <h3 className="mv-tier-name">{c.name}</h3>
                {c.description && <p className="mv-tier-tagline">{c.description}</p>}
                {/* Price + duration hidden per user request — the tier card
                    now shows only the name, benefits, and CTA. Price is
                    still shown on the checkout / payment page.
                <div className="mv-tier-price">{formatMembershipPrice(c)}</div>
                */}
                {highlights.length > 0 && (
                  <ul className="mv-tier-features">
                    {highlights.map((h, i) => (
                      <li key={i}><i className="bi bi-check-circle-fill" />{h}</li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  className="mv-tier-cta"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFormData({
                      ...formData,
                      type: c.baseType === 'couple' ? 2 : 1,
                      categoryId: c.id,
                    });
                    // Scroll toward the benefits panel so the guest sees
                    // their selection acknowledged immediately.
                    setTimeout(() => {
                      const el = document.querySelector('.mv-benefits-panel, .membership-form');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 60);
                  }}
                >
                  Choose {c.name} <i className="bi bi-arrow-right" />
                </button>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  const renderApplicationForm = () => {
    return (
      <form onSubmit={handleSubmit} className="membership-form" noValidate>
        <h2>Apply for Membership</h2>

        {error && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Please fix the following errors:</strong>
            <div className="mt-2">
              {error}
            </div>
          </div>
        )}

        <div className="section">
          <h3>Personal Information</h3>
          <div className="row">
            <div className="col-md-6 mb-3" id="field-name">
              <label>Full Name *</label>
              <input
                type="text"
                className={`form-control ${fieldErrors.name ? 'is-invalid' : ''}`}
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={(e) => validateSingle(e.target.name)}
                required
              />
              {fieldErrors.name && <div className="field-error">{fieldErrors.name}</div>}
            </div>
            <div className="col-md-6 mb-3" id="field-email">
              <label>Email *</label>
              <input
                type="email"
                className={`form-control ${fieldErrors.email ? 'is-invalid' : ''}`}
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={(e) => validateSingle(e.target.name)}
                required
              />
              {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
            </div>
            <div className={`col-md-6 mb-3 ${fieldErrors.phone ? 'has-error' : ''}`} id="field-phone">
              <label>Phone Number *</label>
              <PhoneInput
                country={phoneCountry}
                value={formData.phone}
                onChange={(value) => handlePhoneChange(value, 'phone')}
                onBlur={() => validateSingle('phone')}
                inputClass="form-control"
              />
              {fieldErrors.phone && <div className="field-error">{fieldErrors.phone}</div>}
            </div>
            <div className="col-md-6 mb-3" id="field-socialMediaLink">
              <label>Social Media Link *</label>
              <input
                type="url"
                className={`form-control ${fieldErrors.socialMediaLink ? 'is-invalid' : ''}`}
                name="socialMediaLink"
                value={formData.socialMediaLink}
                onChange={handleChange}
                onBlur={(e) => validateSingle(e.target.name)}
                placeholder="https://instagram.com/yourhandle"
                required
              />
              {fieldErrors.socialMediaLink && (
                <div className="field-error">{fieldErrors.socialMediaLink}</div>
              )}
            </div>
            <div className="col-md-6 mb-3" id="field-birthdate">
              <label>Birthdate</label>
              <input
                type="date"
                className={`form-control ${fieldErrors.birthdate ? 'is-invalid' : ''}`}
                name="birthdate"
                value={formData.birthdate}
                onChange={handleChange}
                onBlur={(e) => validateSingle(e.target.name)}
                max={maxBirthdate()}
              />
              {fieldErrors.birthdate && <div className="field-error">{fieldErrors.birthdate}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label>Gender</label>
              <select className="form-control" name="gender" value={formData.gender} onChange={handleChange}>
                <option value="1">Male</option>
                <option value="2">Female</option>
              </select>
            </div>
            <div className="col-12 mb-3" id="field-photo">
              <label>Your Photo *</label>
              <input
                type="file"
                className={`form-control ${fieldErrors.photo ? 'is-invalid' : ''}`}
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handlePhotoChange(e, 'photo')}
              />
              <small className="form-text text-muted">JPEG, PNG or WebP. Max 5 MB.</small>
              {photoPreview && (
                <div className="mt-2 d-flex align-items-center gap-3">
                  <img
                    src={photoPreview}
                    alt="Your photo preview"
                    style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleRemovePhoto('photo')}
                  >
                    Remove
                  </button>
                </div>
              )}
              {fieldErrors.photo && <div className="field-error">{fieldErrors.photo}</div>}
            </div>
          </div>
        </div>

        {formData.type === 2 && (
          <div className="section">
            <h3>Partner Information</h3>
            <div className="row">
              <div className="col-md-6 mb-3" id="field-coupleName">
                <label>Partner's Full Name *</label>
                <input
                  type="text"
                  className={`form-control ${fieldErrors.coupleName ? 'is-invalid' : ''}`}
                  name="coupleName"
                  value={formData.coupleName}
                  onChange={handleChange}
                  onBlur={(e) => validateSingle(e.target.name)}
                  required={formData.type === 2}
                />
                {fieldErrors.coupleName && <div className="field-error">{fieldErrors.coupleName}</div>}
              </div>
              <div className={`col-md-6 mb-3 ${fieldErrors.couplePhone ? 'has-error' : ''}`} id="field-couplePhone">
                <label>Partner's Phone *</label>
                <PhoneInput
                  country={'ae'}
                  value={formData.couplePhone}
                  onChange={(value) => handlePhoneChange(value, 'couplePhone')}
                  onBlur={() => validateSingle('couplePhone')}
                  inputClass="form-control"
                />
                {fieldErrors.couplePhone && <div className="field-error">{fieldErrors.couplePhone}</div>}
              </div>
              <div className="col-md-6 mb-3" id="field-coupleEmail">
                <label>Partner's Email *</label>
                <input
                  type="email"
                  className={`form-control ${fieldErrors.coupleEmail ? 'is-invalid' : ''}`}
                  name="coupleEmail"
                  value={formData.coupleEmail}
                  onChange={handleChange}
                  onBlur={(e) => validateSingle(e.target.name)}
                  required={formData.type === 2}
                />
                {fieldErrors.coupleEmail && <div className="field-error">{fieldErrors.coupleEmail}</div>}
              </div>
              <div className="col-md-6 mb-3" id="field-coupleSocialMediaLink">
                <label>Partner's Social Media *</label>
                <input
                  type="url"
                  className={`form-control ${fieldErrors.coupleSocialMediaLink ? 'is-invalid' : ''}`}
                  name="coupleSocialMediaLink"
                  value={formData.coupleSocialMediaLink}
                  onChange={handleChange}
                  onBlur={(e) => validateSingle(e.target.name)}
                  placeholder="https://instagram.com/partnerhandle"
                  required={formData.type === 2}
                />
                {fieldErrors.coupleSocialMediaLink && (
                  <div className="field-error">{fieldErrors.coupleSocialMediaLink}</div>
                )}
              </div>
              <div className="col-12 mb-3" id="field-couplePhoto">
                <label>Partner's Photo *</label>
                <input
                  type="file"
                  className={`form-control ${fieldErrors.couplePhoto ? 'is-invalid' : ''}`}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handlePhotoChange(e, 'couplePhoto')}
                />
                <small className="form-text text-muted">JPEG, PNG or WebP. Max 5 MB.</small>
                {couplePhotoPreview && (
                  <div className="mt-2 d-flex align-items-center gap-3">
                    <img
                      src={couplePhotoPreview}
                      alt="Partner's photo preview"
                      style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleRemovePhoto('couplePhoto')}
                    >
                      Remove
                    </button>
                  </div>
                )}
                {fieldErrors.couplePhoto && (
                  <div className="field-error">{fieldErrors.couplePhoto}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Only the Country of Residence is collected. Street, neighborhood
            and additional-info inputs were removed per user request. */}
        <div className="section">
          <h3>Country</h3>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label>Country of Residence <span className="text-danger">*</span></label>
              <select
                className="form-control"
                name="residenceCountry"
                value={formData.residenceCountry}
                onChange={handleResidenceCountryChange}
                required
              >
                <option value="">Select country...</option>
                {COUNTRIES.map((country) => (
                  <option key={country.iso2} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* "What Happens Next" — different steps depending on the org's
            payment mode. Auth-capture goes to Paymob upfront (card is
            authorized, captured later on approval). Manual-link submits
            the application only, then admin approval triggers an email
            with a payment link — no upfront card entry. */}
        {(() => {
          const isManualLink = (pricing?.paymentMode || 'auth_capture') === 'manual_link';
          const steps = isManualLink
            ? [
                { title: 'Submit your application', desc: 'Fill in your details — no payment now.' },
                { title: 'Admin review', desc: 'Our team reviews your application (usually within 24 hours).' },
                { title: 'Receive payment link', desc: 'Once approved, we email you a secure Paymob link to complete payment.' },
                { title: 'Membership activated', desc: 'Pay via the link, and your benefits unlock instantly.' },
              ]
            : [
                { title: 'Submit your application', desc: 'Fill in your details and continue to payment.' },
                { title: 'Authorize payment', desc: 'Securely authorize your card via Paymob — no charge yet.' },
                { title: 'Admin review', desc: 'Our team reviews and approves your application.' },
                { title: 'Membership activated', desc: 'Your card is charged and your benefits begin.' },
              ];
          return (
            <>
              <div className="section">
                <h3>What Happens Next</h3>
                <ol className="payment-steps">
                  {steps.map((s, i) => (
                    <li key={i} className={`payment-step ${i === 0 ? 'is-current' : ''}`}>
                      <span className="payment-step-num">{i + 1}</span>
                      <div className="payment-step-body">
                        <div className="payment-step-title">{s.title}</div>
                        <div className="payment-step-desc">{s.desc}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {/* "Next Step" info box removed per user request — the
                  4-step timeline above already communicates what happens
                  next, so the extra alert is redundant. */}

              <div className="form-actions">
                <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : isManualLink ? (
                    <>
                      <i className="bi bi-send me-2"></i>
                      Submit application
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-right-circle me-2"></i>
                      Continue to Payment
                    </>
                  )}
                </button>
              </div>
            </>
          );
        })()}
      </form>
    );
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container my-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Benefits appear ONLY when the guest has picked a specific membership
  // package. No default benefits are shown while nothing is selected —
  // that way the page stays clean and each tier's benefits reveal
  // themselves as the guest explores the options.
  const selectedCategory = formData.categoryId
    ? (pricing?.categories || []).find((c) => c.id === formData.categoryId)
    : null;
  const activeInfoContent = selectedCategory ? selectedCategory.benefits : null;

  const selectedCatName = selectedCategory?.name;
  return (
    <>
      <Navbar />
      <div className="membership-page-v2">
        <div className="mv-container">
          {/* Post-payment banner — fired when Paymob redirects back with
              ?payment=success|failed|refunded|error. Auto-dismisses after
              8s (URL cleaned so refresh doesn't re-show it). */}
          {paymentStatus && (
            <div className={`mv-payment-banner mv-payment-${paymentStatus}`}>
              <div className="mv-payment-icon">
                {paymentStatus === 'success' && <i className="bi bi-check-circle-fill" />}
                {paymentStatus === 'failed' && <i className="bi bi-x-circle-fill" />}
                {paymentStatus === 'refunded' && <i className="bi bi-arrow-counterclockwise" />}
                {paymentStatus === 'error' && <i className="bi bi-exclamation-triangle-fill" />}
              </div>
              <div className="mv-payment-body">
                <div className="mv-payment-title">
                  {paymentStatus === 'success' && 'Payment received — welcome aboard!'}
                  {paymentStatus === 'failed' && 'Payment could not be completed'}
                  {paymentStatus === 'refunded' && 'Your payment was refunded'}
                  {paymentStatus === 'error' && 'Something went wrong with the payment'}
                </div>
                <div className="mv-payment-desc">
                  {paymentStatus === 'success' && 'Your membership is being activated now. You will receive a confirmation email shortly with your unique member code.'}
                  {paymentStatus === 'failed' && (paymentMessage || 'Your bank declined the charge. Try again, or use a different card.')}
                  {paymentStatus === 'refunded' && 'The charge has been reversed. Please contact us if you have any questions.'}
                  {paymentStatus === 'error' && (paymentMessage || 'We could not confirm your payment. If your bank shows the charge, contact support and we will sort it out.')}
                </div>
              </div>
            </div>
          )}

          <div className="mv-card">

            {/* HERO — driven by pricing.titleLabel + pricing.slogan */}
            <section className="mv-hero">
              <div className="mv-hero-inner">
                <span className="mv-hero-eyebrow"><i className="bi bi-stars" /> Members-only</span>
                <h1 className="mv-hero-title">{pricing?.titleLabel || 'Membership'}</h1>
                <p className="mv-hero-slogan">
                  {pricing?.slogan || 'Join our family and enjoy exclusive benefits'}
                </p>
                {(pricing?.categories?.length || 0) > 0 && (
                  <div className="mv-hero-ctas">
                    <button
                      className="mv-btn-primary"
                      type="button"
                      onClick={() => {
                        const el = document.querySelector('.mv-tiers');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                    >
                      Explore tiers <i className="bi bi-arrow-down" />
                    </button>
                  </div>
                )}
                {user?.role && ['admin', 'manager'].includes(user.role) && (
                  <div className="mv-hero-ctas" style={{ marginTop: 16 }}>
                    <button
                      className="mv-btn-ghost"
                      type="button"
                      onClick={() => navigate(path('/membership/payments'))}
                    >
                      <i className="bi bi-credit-card" /> Payments
                    </button>
                  </div>
                )}
              </div>
            </section>

            {/* TIERS — from pricing.categories */}
            {showForm && renderTypeSelector()}

            {/* BENEFITS PANEL — appears only when the guest picks a tier.
                Content is the selected category's rich-HTML benefits. */}
            {activeInfoContent && (
              <section className="mv-benefits-panel">
                <div className="mv-benefits-header">
                  <div className="mv-benefits-eyebrow">Your selection</div>
                  <div className="mv-benefits-name">{selectedCatName}</div>
                </div>
                <div
                  className="mv-benefits-content"
                  dangerouslySetInnerHTML={{ __html: sanitizeInfoHtml(activeInfoContent) }}
                />
              </section>
            )}

            {/* Existing member card / status message — unchanged */}
            {membershipStatus && membershipStatus.hasMembership && (
              <div style={{ padding: '0 32px 32px' }}>
                {renderMembershipCard()}
                {renderStatusMessage()}
              </div>
            )}

            {!membershipStatus?.hasMembership && (
              <div style={{ padding: '0 32px' }}>
                {renderStatusMessage()}
              </div>
            )}

            {/* APPLICATION FORM — same form logic + validation, restyled
                by the v2 CSS overlay. */}
            {showForm && (
              <section className="mv-section">
                {renderApplicationForm()}
              </section>
            )}

            {!membershipStatus?.hasMembership && !membershipStatus?.canApply && !showForm && (
              <div className="mv-section">
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2" />
                  You cannot apply for membership at this time.
                </div>
              </div>
            )}

            {/* TRUST FOOTER — static, common across orgs */}
            <section className="mv-trust">
              <div className="mv-trust-badges">
                <div className="mv-badge-item"><i className="bi bi-shield-lock-fill" /> Secured by Paymob</div>
                <div className="mv-badge-item"><i className="bi bi-clock-history" /> Cancel anytime</div>
                <div className="mv-badge-item"><i className="bi bi-headset" /> 24/7 support</div>
              </div>
              <p className="mv-trust-copy">
                Your card details never touch our servers. All payments are encrypted end-to-end.
              </p>
            </section>

          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Membership;
